import { Button } from '@/components/ui/button';
import { Form, FormField } from '@/components/ui/form';
import { TooltipProvider } from '@/components/ui/tooltip';
import { useFormsTranslation } from '@/hooks/useTranslation';
import { accountFormService } from '@/services/accountFormService';
import { zodResolver } from '@hookform/resolvers/zod';
import { Plus, Trash2 } from 'lucide-react';
import { useEffect, useImperativeHandle, forwardRef } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { z } from 'zod';
import { FormFieldWrapper } from '../FormFieldWrapper';

// Zod schema for Bank Connection validation
const bankConnectionSchema = z.object({
  currency: z.string().min(1, 'Currency is required'),
  beneficiary: z.string().min(1, 'Beneficiary is required'),
  bankName: z.string().min(1, 'Bank name is required'),
  bankLocation: z.string().min(1, 'Bank location is required'),
  clearingNumber: z.string().min(1, 'Clearing number is required'),
  accountNumber: z.string().min(1, 'Account number is required'),
  iban: z.string().min(1, 'IBAN is required'),
  swiftCode: z.string().min(1, 'SWIFT code is required'),
  bookingText: z.string().optional(),
});

// Schema for multiple bank connections
const bankConnectionsFormSchema = z.object({
  bankConnections: z
    .array(bankConnectionSchema)
    .min(1, 'At least one bank connection is required'),
});

type BankConnectionsFormData = z.infer<typeof bankConnectionsFormSchema>;

/**
 * Bank Connection Form Component
 * Handles multiple bank connection records for payment information
 * @param onSubmit - Callback function when form is submitted
 * @param formData - Initial form data for editing
 */
interface BankConnectionFormProps {
  formData?: Partial<BankConnectionsFormData>;
  onSubmit?: (data: BankConnectionsFormData) => void;
  onChange?: (data: BankConnectionsFormData) => void;
}

export interface BankConnectionFormRef {
  triggerValidation: () => Promise<boolean>;
}

export const BankConnectionForm = forwardRef<
  BankConnectionFormRef,
  BankConnectionFormProps
>(({ onSubmit, onChange, formData = {} }, ref) => {
  const { t: tForms } = useFormsTranslation();

  // Initialize form with react-hook-form
  const form = useForm<BankConnectionsFormData>({
    resolver: zodResolver(bankConnectionsFormSchema),
    defaultValues: {
      bankConnections:
        formData.bankConnections && formData.bankConnections.length > 0
          ? formData.bankConnections
          : [
              {
                currency: '',
                beneficiary: '',
                bankName: '',
                bankLocation: '',
                clearingNumber: '',
                accountNumber: '',
                iban: '',
                swiftCode: '',
                bookingText: '',
              },
            ],
    },
  });

  // Field array for managing multiple bank connections
  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'bankConnections',
  });

  // Real-time form data changes for tab persistence
  useEffect(() => {
    if (onChange) {
      const subscription = form.watch(data => {
        // Send form data back to parent for persistence
        if (data.bankConnections) {
          onChange(data as BankConnectionsFormData);
        }
      });
      return () => subscription.unsubscribe();
    }
  }, [form, onChange]);

  // Ensure at least one bank connection form is always displayed
  useEffect(() => {
    if (fields.length === 0) {
      append({
        currency: '',
        beneficiary: '',
        bankName: '',
        bankLocation: '',
        clearingNumber: '',
        accountNumber: '',
        iban: '',
        swiftCode: '',
        bookingText: '',
      });
    }
  }, [fields.length, append]);

  // Load currency options on component mount
  useEffect(() => {
    const loadCurrencies = async () => {
      try {
        await accountFormService.getCurrencies();
        // Store currencies in a ref or state if needed for form options
      } catch (error) {
        console.error('Failed to load currencies:', error);
      }
    };

    loadCurrencies();
  }, []);

  const handleFormSubmit = (data: BankConnectionsFormData) => {
    if (onSubmit) {
      onSubmit(data);
    }
  };

  // Expose form methods via ref
  useImperativeHandle(
    ref,
    () => ({
      triggerValidation: async () => {
        const isValid = await form.trigger();
        return isValid;
      },
    }),
    [form]
  );

  return (
    <TooltipProvider>
      <Form {...form}>
        <form
          id='account-banking-form'
          onSubmit={form.handleSubmit(handleFormSubmit)}
          className='space-y-6'
        >
          {/* Header */}
          <div className='space-y-2'></div>

          {/* Bank Connections */}
          <div className='space-y-6'>
            {fields.map((field, index) => (
              <div key={field.id} className='space-y-4 border rounded-lg p-4'>
                <div className='flex justify-between items-center mb-4'>
                  <h4 className='text-md font-medium'>
                    {tForms('bankConnection.labels.connectionNumber', {
                      number: index + 1,
                    })}
                  </h4>
                  {fields.length > 1 && (
                    <Button
                      type='button'
                      variant='outline'
                      size='sm'
                      onClick={() => remove(index)}
                      className='text-destructive hover:text-destructive'
                    >
                      <Trash2 className='w-4 h-4 mr-1' />
                      {tForms('bankConnection.buttons.remove')}
                    </Button>
                  )}
                </div>

                {/* Bank Connection Fields Grid */}
                <div className='grid grid-cols-1 md:grid-cols-2 gap-4 items-start'>
                  {/* Currency */}
                  <FormField
                    control={form.control}
                    name={`bankConnections.${index}.currency`}
                    render={({ field, fieldState }) => (
                      <FormFieldWrapper
                        label={tForms('bankConnection.fields.currency')}
                        name={`bankConnections.${index}.currency`}
                        required
                        error={fieldState.error?.message}
                        tooltip={tForms('bankConnection.tooltips.currency')}
                      >
                        <select
                          {...field}
                          className='flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50'
                        >
                          <option value=''>
                            {tForms(
                              'bankConnection.placeholders.selectCurrency'
                            )}
                          </option>
                          <option value='CHF'>CHF – Swiss franc</option>
                          <option value='EUR'>EUR – Euro</option>
                          <option value='USD'>
                            USD – United States dollar
                          </option>
                          <option value='GBP'>GBP – Pound sterling</option>
                          <option value='JPY'>JPY – Japanese yen</option>
                          <option value='CNY'>
                            CNY – Chinese yuan renminbi
                          </option>
                          <option value='AUD'>AUD – Australian dollar</option>
                          <option value='CAD'>CAD – Canadian dollar</option>
                          <option value='NZD'>NZD – New Zealand dollar</option>
                          <option value='SEK'>SEK – Swedish krona</option>
                          <option value='NOK'>NOK – Norwegian krone</option>
                          <option value='DKK'>DKK – Danish krone</option>
                          <option value='PLN'>PLN – Polish złoty</option>
                          <option value='CZK'>CZK – Czech koruna</option>
                          <option value='HUF'>HUF – Hungarian forint</option>
                          <option value='RUB'>RUB – Russian ruble</option>
                          <option value='TRY'>TRY – Turkish lira</option>
                          <option value='BRL'>BRL – Brazilian real</option>
                          <option value='MXN'>MXN – Mexican peso</option>
                          <option value='ARS'>ARS – Argentine peso</option>
                          <option value='CLP'>CLP – Chilean peso</option>
                          <option value='INR'>INR – Indian rupee</option>
                          <option value='KRW'>KRW – South Korean won</option>
                          <option value='ZAR'>ZAR – South African rand</option>
                          <option value='AED'>AED – UAE dirham</option>
                          <option value='SAR'>SAR – Saudi riyal</option>
                          <option value='COP'>COP – Colombian peso</option>
                        </select>
                      </FormFieldWrapper>
                    )}
                  />

                  {/* Beneficiary */}
                  <FormField
                    control={form.control}
                    name={`bankConnections.${index}.beneficiary`}
                    render={({ field, fieldState }) => (
                      <FormFieldWrapper
                        label={tForms('bankConnection.fields.beneficiary')}
                        name={`bankConnections.${index}.beneficiary`}
                        required
                        error={fieldState.error?.message}
                        tooltip={tForms('bankConnection.tooltips.beneficiary')}
                      >
                        <input
                          {...field}
                          type='text'
                          placeholder={tForms(
                            'bankConnection.placeholders.enterBeneficiary'
                          )}
                          className='flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50'
                        />
                      </FormFieldWrapper>
                    )}
                  />

                  {/* Bank Name */}
                  <FormField
                    control={form.control}
                    name={`bankConnections.${index}.bankName`}
                    render={({ field, fieldState }) => (
                      <FormFieldWrapper
                        label={tForms('bankConnection.fields.bankName')}
                        name={`bankConnections.${index}.bankName`}
                        required
                        error={fieldState.error?.message}
                        tooltip={tForms('bankConnection.tooltips.bankName')}
                      >
                        <input
                          {...field}
                          type='text'
                          placeholder={tForms(
                            'bankConnection.placeholders.enterBankName'
                          )}
                          className='flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50'
                        />
                      </FormFieldWrapper>
                    )}
                  />

                  {/* Bank Location */}
                  <FormField
                    control={form.control}
                    name={`bankConnections.${index}.bankLocation`}
                    render={({ field, fieldState }) => (
                      <FormFieldWrapper
                        label={tForms('bankConnection.fields.bankLocation')}
                        name={`bankConnections.${index}.bankLocation`}
                        required
                        error={fieldState.error?.message}
                        tooltip={tForms('bankConnection.tooltips.bankLocation')}
                      >
                        <input
                          {...field}
                          type='text'
                          placeholder={tForms(
                            'bankConnection.placeholders.enterBankLocation'
                          )}
                          className='flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50'
                        />
                      </FormFieldWrapper>
                    )}
                  />

                  {/* Clearing Number */}
                  <FormField
                    control={form.control}
                    name={`bankConnections.${index}.clearingNumber`}
                    render={({ field, fieldState }) => (
                      <FormFieldWrapper
                        label={tForms('bankConnection.fields.clearingNumber')}
                        name={`bankConnections.${index}.clearingNumber`}
                        required
                        error={fieldState.error?.message}
                        tooltip={tForms(
                          'bankConnection.tooltips.clearingNumber'
                        )}
                      >
                        <input
                          {...field}
                          type='text'
                          placeholder={tForms(
                            'bankConnection.placeholders.enterClearingNumber'
                          )}
                          className='flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50'
                        />
                      </FormFieldWrapper>
                    )}
                  />

                  {/* Account Number */}
                  <FormField
                    control={form.control}
                    name={`bankConnections.${index}.accountNumber`}
                    render={({ field, fieldState }) => (
                      <FormFieldWrapper
                        label={tForms('bankConnection.fields.accountNumber')}
                        name={`bankConnections.${index}.accountNumber`}
                        required
                        error={fieldState.error?.message}
                        tooltip={tForms(
                          'bankConnection.tooltips.accountNumber'
                        )}
                      >
                        <input
                          {...field}
                          type='text'
                          placeholder={tForms(
                            'bankConnection.placeholders.enterAccountNumber'
                          )}
                          className='flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50'
                        />
                      </FormFieldWrapper>
                    )}
                  />

                  {/* IBAN */}
                  <FormField
                    control={form.control}
                    name={`bankConnections.${index}.iban`}
                    render={({ field, fieldState }) => (
                      <FormFieldWrapper
                        label={tForms('bankConnection.fields.iban')}
                        name={`bankConnections.${index}.iban`}
                        required
                        error={fieldState.error?.message}
                        tooltip={tForms('bankConnection.tooltips.iban')}
                      >
                        <input
                          {...field}
                          type='text'
                          placeholder={tForms(
                            'bankConnection.placeholders.enterIban'
                          )}
                          className='flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50'
                        />
                      </FormFieldWrapper>
                    )}
                  />

                  {/* SWIFT Code */}
                  <FormField
                    control={form.control}
                    name={`bankConnections.${index}.swiftCode`}
                    render={({ field, fieldState }) => (
                      <FormFieldWrapper
                        label={tForms('bankConnection.fields.swiftCode')}
                        name={`bankConnections.${index}.swiftCode`}
                        required
                        error={fieldState.error?.message}
                        tooltip={tForms('bankConnection.tooltips.swiftCode')}
                      >
                        <input
                          {...field}
                          type='text'
                          placeholder={tForms(
                            'bankConnection.placeholders.enterSwiftCode'
                          )}
                          className='flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50'
                        />
                      </FormFieldWrapper>
                    )}
                  />
                </div>

                {/* Booking Text (full width) */}
                <FormField
                  control={form.control}
                  name={`bankConnections.${index}.bookingText`}
                  render={({ field, fieldState }) => (
                    <FormFieldWrapper
                      label={tForms('bankConnection.fields.bookingText')}
                      name={`bankConnections.${index}.bookingText`}
                      error={fieldState.error?.message}
                      tooltip={tForms('bankConnection.tooltips.bookingText')}
                    >
                      <textarea
                        {...field}
                        placeholder={tForms(
                          'bankConnection.placeholders.enterBookingText'
                        )}
                        rows={4}
                        className='flex min-h-[100px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 resize-vertical'
                      />
                    </FormFieldWrapper>
                  )}
                />
              </div>
            ))}

            {/* Add Bank Connection Button */}
            <Button
              type='button'
              variant='outline'
              onClick={() =>
                append({
                  currency: '',
                  beneficiary: '',
                  bankName: '',
                  bankLocation: '',
                  clearingNumber: '',
                  accountNumber: '',
                  iban: '',
                  swiftCode: '',
                  bookingText: '',
                })
              }
              className='w-full'
            >
              <Plus className='w-4 h-4 mr-2' />
              {tForms('bankConnection.buttons.addConnection')}
            </Button>
          </div>
        </form>
      </Form>
    </TooltipProvider>
  );
});

export default BankConnectionForm;
